<template>
  <transition name="translate-x">
    <div
      v-if="open"
      data-testid="mobile-menu"
      class="mobile-menu fixed left-0 top-11 z-[998] flex w-full flex-col items-center bg-white md:top-20 xl:hidden"
    >
      <div v-show="hasTitle" class="bg-s-100" @click="backPreviousMenu">
        <button
          aria-label="Back Mobile Button"
          class="absolute left-0 top-3 ml-5 p-1"
        >
          <IconLeftAngle class="h-4 w-4" />
        </button>

        <div
          class="w-screen border-b border-solid border-s-600 py-3 pl-12 text-lg font-semibold text-s-900"
        >
          {{ menuTitle }}
        </div>
      </div>

      <section
        class="w-screen overflow-y-auto px-5 pb-2"
        :class="[hasTitle ? 'bg-white' : 'bg-s-100']"
      >
        <MobileMenuItem
          v-for="item in menuList"
          :key="item.name"
          :item="item"
          @handle-click="handleClick"
        />

        <MobileMenuItem v-if="!hasTitle" @handle-click="closeMobileMenu">
          <div class="flex w-full flex-col gap-y-4">
            <button
              v-if="isImpersonating"
              class="cursor-pointer select-none border-b border-s-400 pb-3 text-left text-base font-normal"
              @click="restoreOriginalSession"
            >
              Stop impersonation session
            </button>

            <BaseLink
              href="https://www.benchmarkminerals.com/contact"
              class="px-4 py-2 leading-none text-base h-www-button font-bold"
            >
              Contact Us
            </BaseLink>
          </div>
        </MobileMenuItem>

        <MobileMenuItem v-if="!hasTitle" @handle-click="signInOut">
          {{ signInOutText }}
        </MobileMenuItem>
      </section>
    </div>
  </transition>
</template>

<script setup lang="ts">
import IconLeftAngle from '~/assets/icons/angle-left.svg'
import MobileMenuItem from '~/components/navigation/MobileMenuItem.vue'
import { useAuthStore } from '~/stores/auth'

const props = withDefaults(
  defineProps<{
    menu?: Array<any>
    initialMenuTitle?: string
    open?: boolean
    modalName: string
  }>(),
  {
    data: () => [],
    initialMenuTitle: 'Menu',
    open: false,
  },
)

const router = useRouter()
const authStore = useAuthStore()
const { $modal, $auth } = useNuxtApp()

const menuList = ref(props.menu)
const menuTitle = ref(props.initialMenuTitle)
const previousMenuList = ref([])

const hasTitle = computed(() => menuTitle.value !== props.initialMenuTitle)
const authenticated = computed(() => authStore.authenticated)
const groups = computed(() => props.menu.flatMap((e) => e.groups))
const signInOutText = computed(() =>
  authenticated.value ? 'Sign Out' : 'Sign In',
)

const signInOut = () => {
  if (authenticated.value) {
    logout()
  } else {
    signIn()
  }
}

const isImpersonating = computed(() => $auth.isImpersonating())

const restoreOriginalSession = () => {
  $auth.restoreOriginalSession()
  window.location.href = '/'
}

const signIn = () => {
  closeMobileMenu()
  $modal.open('login')
}

const handleClick = async (item) => {
  const newMenuList = item?.children || item?.groups

  if (newMenuList?.length) {
    menuTitle.value = item.name
    previousMenuList.value = menuList.value
    menuList.value = newMenuList
    return
  }

  if (item?.name?.toLowerCase() === 'source') {
    window.location.href = item.href
    closeMobileMenu()
    return
  }

  const route = item?.parent ? `${item.parent}${item.href}` : item.href
  const isExternal = !!route && route.startsWith('http')

  if (!isExternal) await router.push(route)
  else window.open(route, '_blank')

  closeMobileMenu()
}

const resetMenu = () => {
  menuTitle.value = props.initialMenuTitle
  menuList.value = props.menu
}

const closeMobileMenu = () => {
  $modal.close(props.modalName)
  document.body.classList.remove('overflow-hidden')
  resetMenu()
}

const backPreviousMenu = () => {
  const menuInGroupsLevel = groups.value.find((e) => e.name === menuTitle.value)

  let previousMenu = props.menu
  let parentMenu = props.initialMenuTitle
  if (menuInGroupsLevel) {
    for (const item of props.menu) {
      if (item.groups) {
        const group = item.groups.find(
          (g) => g.name === menuInGroupsLevel.name && !g.initial,
        )
        if (group) {
          parentMenu = item.name
          previousMenu = item.groups
          break
        }
      }
    }

    if (previousMenu === props.menu) {
      previousMenu = previousMenuList.value
    }
  }

  menuTitle.value = parentMenu
  menuList.value = previousMenu
}

const logout = async () => {
  $auth.logout()
  closeMobileMenu()
  window.location.href = '/'
}
</script>

<style scoped>
.mobile-menu {
  height: calc(100dvh - 48px);
}

@media (min-width: 768px) {
  .mobile-menu {
    height: calc(100dvh - 80px);
  }
}

.translate-x-enter {
  transform: translateX(-100%);
}

.translate-x-enter-active,
.translate-x-leave-active {
  transition: 0.5s ease;
}

.translate-x-enter-from,
.translate-x-leave-to {
  transform: translateX(-100%);
}
</style>
